/**
 * The one call this app makes: POST /api/panic.
 *
 * Same origin, because the API is a Pages Function inside the same Pages project.
 * No CORS, no second host, no preflight.
 */

import { COOLDOWN_SECONDS, PANIC_TIMEOUT_MS } from './limits';

export const PANIC_ENDPOINT = '/api/panic';
export const SESSION_ENDPOINT = '/api/session';

/**
 * Hard ceiling on the request, defined in limits.ts next to the Function's own
 * upstream budget because the relationship between the two is the whole point.
 * A spinner that never resolves is worse than a clear failure with a retry: the
 * user needs to know quickly whether to pick up a phone.
 */
export { PANIC_TIMEOUT_MS };

/** Whole seconds, for copy that has to state the budget without repeating it. */
export const PANIC_TIMEOUT_SECONDS = Math.round(PANIC_TIMEOUT_MS / 1000);

export interface TriggerPanicInput {
  /** Google id_token, sent as the Bearer token and verified server side. */
  credential: string;
  /** Optional free text from the user, already trimmed by the caller. */
  note?: string;
  /**
   * Send even though an alert was raised in the last few minutes. Only ever set
   * from a second, deliberate hold after the cooldown panel has explained that one
   * is already open.
   */
  override?: boolean;
}

export type PanicResult =
  /**
   * The alert is open in OpsGenie. alertId is null if the API answered without
   * one, and responderTeams is empty when the Function had no OPSGENIE_TEAM to put
   * on the alert, which means nothing here proves a phone rang.
   */
  | { kind: 'ok'; alertId: string | null; responderTeams: string[] }
  /** An alert for this window already exists, nobody needs paging twice. */
  | { kind: 'cooldown'; retryAfterSeconds: number; alertId: string | null; message: string | null }
  /** Token missing, expired or not verifiable. A fresh sign in is the fix. */
  | { kind: 'unauthorized'; message: string | null }
  /** Verified identity, but the domain is not allowed to page. */
  | { kind: 'forbidden'; message: string | null }
  /** The Function or OpsGenie failed. Retryable. */
  | { kind: 'upstream_error'; status: number; message: string | null }
  /** The request never got an answer. */
  | { kind: 'network_error'; timedOut: boolean };

interface PanicResponseBody {
  alertId?: unknown;
  /** Seconds left on the cooldown. The field name the Function actually sends. */
  retryAfterSeconds?: unknown;
  /** Responder team the alert was sent to, null when none was configured. */
  responderTeams?: unknown;
  message?: unknown;
  error?: unknown;
}

/** Bodies are best effort: a 502 from the edge itself is HTML, not JSON. */
async function readBody(response: Response): Promise<PanicResponseBody> {
  try {
    const parsed: unknown = await response.json();
    if (parsed === null || typeof parsed !== 'object') return {};
    return parsed as PanicResponseBody;
  } catch {
    return {};
  }
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

/**
 * An empty array is the honest answer for anything that is not a list of
 * non-empty strings, including a missing field or an older Function still sending
 * the previous single-team shape. The UI treats empty as "nobody was routed" and
 * warns, which is the safe direction: it can be wrong about a working config, it
 * must never be silently reassuring about a broken one.
 */
function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === 'string' && entry.trim() !== '');
}

function messageOf(body: PanicResponseBody): string | null {
  return asString(body.message) ?? asString(body.error);
}

/**
 * Seconds until another press can do anything. `retryAfterSeconds` in the body is
 * what the Function sends and is authoritative, the Retry-After header is the
 * fallback for a 429 produced by something other than our Function (an edge rate
 * limiter, for instance), and only then does the shared constant apply.
 */
function retryAfterSeconds(response: Response, body: PanicResponseBody): number {
  const fromBody = body.retryAfterSeconds;
  if (typeof fromBody === 'number' && Number.isFinite(fromBody) && fromBody > 0) {
    return Math.ceil(fromBody);
  }
  const header = response.headers.get('Retry-After');
  if (header !== null) {
    const parsed = Number.parseInt(header, 10);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return COOLDOWN_SECONDS;
}

/**
 * Fires the panic request. Never throws for an HTTP status the design accounts
 * for: every expected outcome comes back as a variant the UI renders. Only a
 * genuine transport failure rejects inside fetch, and that becomes network_error.
 */
export async function triggerPanic({ credential, note, override }: TriggerPanicInput): Promise<PanicResult> {
  let response: Response;
  try {
    response = await fetch(PANIC_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${credential}`,
      },
      // The note is the only field. Email, name and domain come from the verified
      // token server side, never from this body.
      body: JSON.stringify({
        ...(note !== undefined && note.length > 0 ? { note } : {}),
        ...(override === true ? { override: true } : {}),
      }),
      signal: AbortSignal.timeout(PANIC_TIMEOUT_MS),
      // A cached panic response would be catastrophic.
      cache: 'no-store',
      credentials: 'omit',
    });
  } catch (error) {
    // AbortSignal.timeout aborts with a TimeoutError. Older engines report the
    // generic AbortError, so both count as a timeout here.
    const name = error instanceof Error ? error.name : '';
    return { kind: 'network_error', timedOut: name === 'TimeoutError' || name === 'AbortError' };
  }

  const body = await readBody(response);

  if (response.ok) {
    return {
      kind: 'ok',
      alertId: asString(body.alertId),
      responderTeams: asStringArray(body.responderTeams),
    };
  }

  switch (response.status) {
    case 429:
      return {
        kind: 'cooldown',
        retryAfterSeconds: retryAfterSeconds(response, body),
        alertId: asString(body.alertId),
        message: messageOf(body),
      };
    case 401:
      return { kind: 'unauthorized', message: messageOf(body) };
    case 403:
      return { kind: 'forbidden', message: messageOf(body) };
    default:
      return { kind: 'upstream_error', status: response.status, message: messageOf(body) };
  }
}

/**
 * Asks the server whether the signed in account may page on-call.
 *
 * The browser cannot answer this itself: the allowlist is server side, precisely
 * so the list of domains that reach an on-call engineer is not sitting in a public
 * bundle. Without this call the UI would have to show the panic button to everyone
 * and let a disallowed account discover the truth after a two second hold.
 *
 * `unknown` is a deliberate third answer, returned when the check itself failed
 * (Google's keys unreachable, no network, a 500). The caller must treat it as
 * "show the button": POST /api/panic re-verifies everything anyway, so the worst
 * case is a 403 on press, while hiding the button because a check failed would
 * take the panic button away during exactly the kind of incident it exists for.
 */
export type SessionCheck =
  | { kind: 'allowed'; email: string | null }
  | { kind: 'denied'; email: string | null }
  | { kind: 'unauthorized' }
  | { kind: 'unknown' };

export async function checkSession(credential: string): Promise<SessionCheck> {
  try {
    const response = await fetch(SESSION_ENDPOINT, {
      method: 'POST',
      headers: { authorization: `Bearer ${credential}` },
      // Much shorter than the panic budget. This only gates a button, and a slow
      // answer must not delay someone reaching the thing they came here for.
      signal: AbortSignal.timeout(8000),
    });

    if (response.status === 401) return { kind: 'unauthorized' };
    if (!response.ok) return { kind: 'unknown' };

    const body = (await response.json()) as { allowed?: unknown; email?: unknown };
    const email = typeof body.email === 'string' ? body.email : null;
    // Only an explicit `true` is an allow. Anything else, including a malformed
    // body, is not treated as permission.
    if (body.allowed === true) return { kind: 'allowed', email };
    if (body.allowed === false) return { kind: 'denied', email };
    return { kind: 'unknown' };
  } catch {
    return { kind: 'unknown' };
  }
}
